import { type PersistStorage, persist } from "zustand/middleware";
import { createStore } from "zustand/vanilla";
import { toast } from "@/components/ui/sonner";
import type { ConfigurationItemAddon } from "@/features/addons/types/configuration-item-addon.type";
import type { ContactInfoReturnType } from "@/features/checkout/forms";
import type {
  DietCatalog,
  DietCatalogCateringType,
} from "@/features/diets/types";
import {
  getObfuscatedLocalStorage,
  setObfuscatedLocalStorage,
} from "@/lib/storage";
import type { CartUpdateAction, ItemPriceBody } from "../types";
import type { Cart } from "../types/cart.type";
import type { OrderInvoice } from "../types/invoice-data.type";

type CartState = Cart & {
  // cartId is taken from the API - it's a unique identifier for the cart
  // It's used to identify the cart in the backend
  cartId?: string;

  // if the user created an order, we should store it for updating the cart
  currentOrderId?: string;

  // pending items
  pendingItems?: ItemPriceBody[];
  pendingEditItems?: (ItemPriceBody & { id: string })[];

  // invoices
  invoiceData: OrderInvoice | null;
  invoiceActive: boolean;
  invoiceUseSameAddress?: boolean;

  // address error
  addressNotSupported?: boolean | null;

  // disabled items
  disabledItems?: string[];

  // delivery costs
  deliveryCosts?: {
    itemId: string;
    dietId: string;
    cost: DietCatalogCateringType["deliveryCosts"];
  }[];

  // order processing state
  isProcessingOrder: boolean;

  // global loading state
  isLoading: boolean;

  // addons selected per diet configuration item, keyed by configuration item id.
  // Mirrors ConfigurationItem.addons (MPL-3693) so it is ready for PUT /cart/{id}.
  addonSelections: Record<string, ConfigurationItemAddon[]>;

  // True once zustand/persist has read localStorage. During React hydration
  // useStore serves the initial (empty) state, not the persisted one, so
  // effects that must see the real cart wait for this flag (MPL-3932).
  hasHydrated: boolean;
};

type CartActions = {
  // cartId
  setCartId: (cartId: string) => void;
  setCart: (cart: Cart) => void;
  resetCart: () => void;

  // update cart
  updateCart: (
    cartId: string,
    cart: Cart,
    action: CartUpdateAction,
    t?: (key: string) => string,
  ) => Cart;

  // as id1,id2,id3
  getDietIds: () => string;

  setOrderId: (orderId: string) => void;
  getAddress: () => Partial<ContactInfoReturnType> | undefined;
  getInvoiceData: () => OrderInvoice | null;
  setInvoiceData: (invoiceData: OrderInvoice) => void;
  setInvoiceUseSameAddress: (useSameAddress: boolean) => void;

  // pending items actions
  addPendingItem: (item: ItemPriceBody) => void;
  addPendingEditItem: (item: ItemPriceBody, id: string) => void;

  // address error
  setAddressNotSupported: (value: boolean) => void;

  // disabled items
  setDisabledItems: (items: string[]) => void;

  addDeliveryCost: (
    itemId: string,
    dietId: string,
    cost: DietCatalogCateringType["deliveryCosts"],
  ) => void;

  removeDeliveryCost: (itemId: string) => void;
  clearDeliveryCost: () => void;
  getDeliveryCost: () => DietCatalogCateringType["deliveryCosts"] | null;
  setDeliveryCosts: (diets: DietCatalog[]) => void;

  // order processing
  setIsProcessingOrder: (value: boolean) => void;
  setIsLoading: (value: boolean) => void;
  setHasHydrated: (value: boolean) => void;

  // addon selections per diet configuration item
  getItemAddons: (itemId: string) => ConfigurationItemAddon[];
  addAddon: (itemId: string, addon: ConfigurationItemAddon) => void;
  updateAddon: (itemId: string, addon: ConfigurationItemAddon) => void;
  removeAddon: (itemId: string, addonId: string, date: string) => void;
  clearItemAddons: (itemId: string) => void;
};

type CartStore = CartState & CartActions;

const defaultValues: CartState = {
  cartId: undefined,
  currentOrderId: undefined,
  addressNotSupported: null,
  pendingItems: [],
  items: [],
  disabledItems: [],
  pendingEditItems: [],
  pricing: {
    final: {
      gross: 0,
      net: 0,
      tax: 0,
    },
    before: {
      gross: 0,
      net: 0,
      tax: 0,
    },
    discounts: [],
    delivery: {
      gross: 0,
      net: 0,
      tax: 0,
    },
  },
  discountCode: null,
  // Foodango products and the total that includes them (MPL-3829).
  productItems: [],
  totalPricing: null,
  invoiceData: null,
  invoiceActive: false,
  agreements: {
    cateringPolicy: false,
    terms: false,
    marketing: false,
    privacy: false,
  },
  isProcessingOrder: false,
  isLoading: false,
  addonSelections: {},
  hasHydrated: false,
};

const normalizeCart = (cart: Cart): Cart => ({
  ...cart,
  productItems: cart.productItems ?? [],
  totalPricing: cart.totalPricing ?? null,
});

const storage: PersistStorage<CartState> = {
  getItem: (name: string) => {
    return getObfuscatedLocalStorage(name);
  },
  setItem: (name: string, value: unknown) => {
    setObfuscatedLocalStorage(name, value);
  },
  removeItem: (name: string) => {
    localStorage.removeItem(name);
  },
};

const createCartStore = ({ storageKey }: { storageKey: string }) =>
  createStore<CartStore>()(
    persist(
      (set, get) => ({
        // default state
        ...defaultValues,

        // cartId actions
        setCartId: (cartId: string) => set({ cartId }),
        // A cart from a backend without products carries neither field; make
        // that explicit instead of keeping whatever the previous cart had.
        setCart: (cart: Cart) => {
          return set(normalizeCart(cart));
        },
        // A reset happens on the client, after hydration — keep the flag up.
        resetCart: () => set({ ...defaultValues, hasHydrated: true }),

        // update cart actions
        updateCart: (cartId, cart, action, t) => {
          get().setCartId(cartId);
          set(normalizeCart(cart));
          set({ pendingItems: [], pendingEditItems: [] });

          const translate = (message: string) => (t ? t(message) : message);

          // show toast message based on the action
          switch (action) {
            case "add":
              toast.success(translate("cart.added"));
              break;
            case "remove":
              toast.info(translate("cart.removed"));
              break;
            case "clear":
              toast.info(translate("cart.cleared"));
              break;
            case "update":
              toast.success(translate("cart.updated"));
              break;
            case "addDiscount":
              toast.success(translate("cart.addedDiscount"));
              break;
            case "addWrongDiscount":
              toast.error(translate("cart.addedWrongDiscount"));
              break;
            case "removeDiscount":
              toast.info(translate("cart.removedDiscount"));
              break;
            default:
              break;
          }

          return get();
        },

        getDietIds: () => {
          return get()
            .items.map((item) => item.configurationItem.dietId)
            .join(",");
        },

        setOrderId: (orderId: string) => set({ currentOrderId: orderId }),

        getAddress: () => {
          const items = get().items;
          if (items.length > 0) {
            const item = items[0]?.configurationItem;
            if (item) {
              const contactStore: Partial<ContactInfoReturnType> = {
                address: item.address,
                email: item.contact?.email,
                phone: item.contact?.phoneNumber,
                firstName: item.contact?.firstName,
                lastName: item.contact?.lastName,
              };
              return contactStore;
            }
          }
        },

        getInvoiceData: () => {
          if (get().invoiceActive && get().invoiceData) {
            const data = get().invoiceData!;
            return {
              active: true,
              companyName: data.companyName,
              vatNumber: data.vatNumber,
              postCode: data.postCode,
              city: data.city,
              street: data.street,
              buildNumber: data.buildNumber,
              flatNumber: data.flatNumber,
            };
          }
          return null;
        },

        setInvoiceData: (invoiceData: OrderInvoice | null) => {
          set({ invoiceData });
        },

        setInvoiceUseSameAddress: (useSameAddress: boolean) =>
          set({ invoiceUseSameAddress: useSameAddress }),

        addPendingItem: (item: ItemPriceBody) => {
          set((state) => ({
            pendingItems: [...(state.pendingItems || []), item],
          }));
        },

        addPendingEditItem: (item: ItemPriceBody, id: string) => {
          set((state) => ({
            pendingEditItems: [
              ...(state.pendingEditItems || []),
              { ...item, id },
            ],
          }));
        },

        setAddressNotSupported: (value: boolean) =>
          set({ addressNotSupported: value }),

        setDisabledItems: (items: string[]) => set({ disabledItems: items }),

        addDeliveryCost: (itemId, dietId, cost) => {
          set((state) => ({
            deliveryCosts: [
              ...(state.deliveryCosts || []),
              { itemId, dietId, cost },
            ],
          }));
        },

        removeDeliveryCost: (itemId) => {
          set((state) => ({
            deliveryCosts: (state.deliveryCosts || []).filter(
              (item) => item.itemId !== itemId,
            ),
          }));
        },

        clearDeliveryCost: () => {
          set({ deliveryCosts: [] });
        },

        getDeliveryCost: () => {
          if (get().items.length === 0) {
            return null;
          }
          const costs = get().deliveryCosts?.map((cost) => cost.cost);
          if (!costs) {
            return null;
          }

          if (
            costs.every(
              (val) => JSON.stringify(val) === JSON.stringify(costs[0]),
            ) &&
            costs[0]
          ) {
            return costs[0];
          }

          // if there are multiple delivery costs, return { type: 'multiple', price: null }
          if (
            costs.length > 1 &&
            costs.some(
              (val) => JSON.stringify(val) !== JSON.stringify(costs[0]),
            )
          ) {
            return { type: "multiple", price: null };
          }

          return null;
        },
        setIsLoading: (value: boolean) => set({ isLoading: value }),

        setHasHydrated: (value: boolean) => set({ hasHydrated: value }),

        setIsProcessingOrder: (value: boolean) =>
          set({ isProcessingOrder: value }),

        // check delivery cost integrity
        setDeliveryCosts: (diets: DietCatalog[]) => {
          const deliveryCosts = get().deliveryCosts || [];

          // check if all items are in the delivery costs
          const items = get().items;

          // if there are no items, return
          if (!items.length) {
            return;
          }

          const missingItems = items.filter(
            (item) =>
              !deliveryCosts.some(
                (cost) => cost.itemId === item.configurationItem.id,
              ),
          );

          // if there are no missing items, return
          if (!missingItems.length) {
            return;
          }

          const missingCosts = missingItems.map((item) => {
            const diet = diets.find(
              (d) => d.id === item.configurationItem.dietId,
            );

            if (!(diet && item.configurationItem.id)) {
              return null;
            }

            return {
              itemId: item.configurationItem.id,
              dietId: diet.id,
              cost: diet.catering.deliveryCosts,
            };
          });

          for (const diet of missingCosts) {
            if (!diet) {
              return;
            }
            get().addDeliveryCost(diet.itemId, diet.dietId, diet.cost);
          }
        },

        getItemAddons: (itemId) => get().addonSelections[itemId] ?? [],

        addAddon: (itemId, addon) => {
          set((state) => {
            const current = state.addonSelections[itemId] ?? [];
            const existing = current.find(
              (item) =>
                item.addonId === addon.addonId && item.date === addon.date,
            );

            // Same addon on the same day accumulates quantity instead of
            // creating a duplicate entry.
            const next = existing
              ? current.map((item) =>
                  item === existing
                    ? { ...item, quantity: item.quantity + addon.quantity }
                    : item,
                )
              : [...current, addon];

            return {
              addonSelections: { ...state.addonSelections, [itemId]: next },
            };
          });
        },

        updateAddon: (itemId, addon) => {
          set((state) => {
            const current = state.addonSelections[itemId] ?? [];

            // A non-positive quantity removes the selection for that day.
            const next =
              addon.quantity > 0
                ? current.map((item) =>
                    item.addonId === addon.addonId && item.date === addon.date
                      ? { ...item, quantity: addon.quantity }
                      : item,
                  )
                : current.filter(
                    (item) =>
                      !(
                        item.addonId === addon.addonId &&
                        item.date === addon.date
                      ),
                  );

            return {
              addonSelections: { ...state.addonSelections, [itemId]: next },
            };
          });
        },

        removeAddon: (itemId, addonId, date) => {
          set((state) => ({
            addonSelections: {
              ...state.addonSelections,
              [itemId]: (state.addonSelections[itemId] ?? []).filter(
                (item) => !(item.addonId === addonId && item.date === date),
              ),
            },
          }));
        },

        clearItemAddons: (itemId) => {
          set((state) => {
            const { [itemId]: _removed, ...rest } = state.addonSelections;
            return { addonSelections: rest };
          });
        },
      }),
      {
        // The key to use for storing the state in local storage.
        name: storageKey,
        storage,
        onRehydrateStorage: () => (state) => {
          state?.setHasHydrated(true);
        },
      },
    ),
  );

export { createCartStore, type CartStore };
